/**
 * useInsights Hook - Auto-generates financial insights from data
 *
 * Analyzes financial data and generates actionable insights including:
 * - YoY growth comparison
 * - Client concentration warnings (>20% = warning)
 * - Margin health distribution
 *
 * Performance target: <100ms generation time (memoized)
 */

import { useMemo, useRef } from 'react';
import type {
  Insight,
  InsightType,
  InsightGeneratorInput,
  UseInsightsReturn,
  ClientAggregation,
} from '../types/insights';

/**
 * Threshold constants
 */
const YOY_GROWTH_POSITIVE_THRESHOLD = 10; // >10% = positive
const YOY_GROWTH_NEGATIVE_THRESHOLD = -10; // <-10% = negative
const CLIENT_CONCENTRATION_WARNING_THRESHOLD = 20; // >20% = warning
const TOP_CLIENTS_CONCENTRATION_THRESHOLD = 50; // Top 3 clients >50% = warning
const MARGIN_HEALTHY_THRESHOLD = 15; // >=15% = healthy
const MARGIN_CRITICAL_THRESHOLD = 5; // <5% = critical

/**
 * Margin bucket definitions
 */
const MARGIN_BUCKETS = {
  excellent: { min: 25, label: 'excelente (≥25%)' },
  good: { min: 15, max: 24.99, label: 'boa (15-24%)' },
  moderate: { min: 5, max: 14.99, label: 'moderada (5-14%)' },
  critical: { max: 4.99, label: 'crítica (<5%)' },
} as const;

/**
 * Generate a unique ID for insights
 */
function generateInsightId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Format percentage for display
 */
function formatPercent(value: number): string {
  return `${Math.abs(value).toFixed(1)}%`;
}

/**
 * Generate YoY growth insight
 */
function generateYoYGrowthInsight(
  currentTotal: number,
  previousYearTotal: number | undefined
): Insight | null {
  if (previousYearTotal === undefined || previousYearTotal === 0) {
    return null;
  }

  const growthPercent = ((currentTotal - previousYearTotal) / Math.abs(previousYearTotal)) * 100;

  let type: InsightType;
  let icon: string;
  let title: string;
  let detail: string;

  if (growthPercent > YOY_GROWTH_POSITIVE_THRESHOLD) {
    type = 'positive';
    icon = '';
    title = `Crescimento de ${formatPercent(growthPercent)}`;
    detail = `Crescimento sólido de ${formatPercent(growthPercent)} em relação ao mesmo período do ano anterior.`;
  } else if (growthPercent < YOY_GROWTH_NEGATIVE_THRESHOLD) {
    type = 'negative';
    icon = '';
    title = `Queda de ${formatPercent(growthPercent)}`;
    detail = `Queda de ${formatPercent(growthPercent)} em relação ao mesmo período do ano anterior.`;
  } else {
    type = 'neutral';
    icon = '';
    title = 'Estável';
    detail = `Variação de ${growthPercent >= 0 ? '+' : ''}${formatPercent(growthPercent)} em relação ao ano anterior - dentro da faixa de estabilidade.`;
  }

  return {
    id: generateInsightId('yoy'),
    type,
    icon,
    title,
    detail,
  };
}

/**
 * Generate client concentration insights
 */
function generateClientConcentrationInsights(
  clientAggregations: ClientAggregation[] | undefined,
  totalRevenue: number
): Insight[] {
  if (!clientAggregations || clientAggregations.length === 0 || totalRevenue === 0) {
    return [];
  }

  const insights: Insight[] = [];

  // Calculate concentration percentages
  const clientsWithConcentration = clientAggregations
    .map(client => ({
      ...client,
      concentrationPercent: (client.revenue / totalRevenue) * 100,
    }))
    .sort((a, b) => b.concentrationPercent - a.concentrationPercent);

  // Check for individual high concentration clients (>20%)
  const highConcentrationClients = clientsWithConcentration.filter(
    c => c.concentrationPercent > CLIENT_CONCENTRATION_WARNING_THRESHOLD
  );

  for (const client of highConcentrationClients) {
    insights.push({
      id: generateInsightId(`concentration-${client.clientId}`),
      type: 'warning',
      icon: '',
      title: 'Alta concentração de cliente',
      detail: `${client.clientName} representa ${client.concentrationPercent.toFixed(1)}% da receita total.`,
      actionLink: `/financial/client-matrix?client=${encodeURIComponent(client.clientName)}`,
      actionLabel: 'Ver detalhes',
    });
  }

  // Check top 3 clients concentration
  const top3Concentration = clientsWithConcentration
    .slice(0, 3)
    .reduce((sum, c) => sum + c.concentrationPercent, 0);

  if (top3Concentration > TOP_CLIENTS_CONCENTRATION_THRESHOLD && highConcentrationClients.length === 0) {
    const top3Names = clientsWithConcentration
      .slice(0, 3)
      .map(c => c.clientName)
      .join(', ');

    insights.push({
      id: generateInsightId('top3-concentration'),
      type: 'warning',
      icon: '',
      title: 'Concentração na base de clientes',
      detail: `Os 3 maiores clientes (${top3Names}) representam ${top3Concentration.toFixed(1)}% da receita. Considere diversificação de carteira.`,
    });
  }

  return insights;
}

/**
 * Generate margin health insights
 */
function generateMarginHealthInsights(
  clientAggregations: ClientAggregation[] | undefined
): Insight[] {
  if (!clientAggregations || clientAggregations.length === 0) {
    return [];
  }

  const insights: Insight[] = [];

  // Count clients per margin bucket
  const bucketCounts = {
    excellent: 0,
    good: 0,
    moderate: 0,
    critical: 0,
  };

  const criticalClients: string[] = [];

  for (const client of clientAggregations) {
    if (client.margin >= MARGIN_BUCKETS.excellent.min) {
      bucketCounts.excellent++;
    } else if (client.margin >= MARGIN_BUCKETS.good.min) {
      bucketCounts.good++;
    } else if (client.margin >= MARGIN_BUCKETS.moderate.min) {
      bucketCounts.moderate++;
    } else {
      bucketCounts.critical++;
      criticalClients.push(client.clientName);
    }
  }

  const totalClients = clientAggregations.length;
  const healthyClients = bucketCounts.excellent + bucketCounts.good;
  const healthyPercent = (healthyClients / totalClients) * 100;

  // Generate positive insight if majority healthy
  if (healthyPercent >= 60) {
    insights.push({
      id: generateInsightId('margin-healthy'),
      type: 'positive',
      icon: '',
      title: 'Margens saudáveis',
      detail: `${healthyClients} de ${totalClients} clientes (${healthyPercent.toFixed(0)}%) apresentam margens saudáveis (≥15%).`,
    });
  }

  // Generate warning if multiple clients below critical threshold
  if (bucketCounts.critical >= 2) {
    const criticalNames = criticalClients.slice(0, 3).join(', ');
    const moreText = criticalClients.length > 3 ? ` e mais ${criticalClients.length - 3}` : '';

    insights.push({
      id: generateInsightId('margin-critical'),
      type: 'warning',
      icon: '',
      title: 'Margens críticas',
      detail: `${bucketCounts.critical} clientes com margem abaixo de 5%: ${criticalNames}${moreText}. Requer atenção.`,
    });
  } else if (bucketCounts.critical === 1) {
    insights.push({
      id: generateInsightId('margin-single-critical'),
      type: 'warning',
      icon: '',
      title: 'Atenção à margem',
      detail: `${criticalClients[0]} apresenta margem crítica (<5%). Avalie renegociação ou otimização de custos.`,
      actionLink: `/financial/client-matrix?client=${encodeURIComponent(criticalClients[0])}`,
      actionLabel: 'Ver cliente',
    });
  }

  return insights;
}

/**
 * useInsights Hook
 *
 * Generates financial insights from provided data.
 * Memoized to ensure <100ms generation time.
 *
 * @param input - InsightGeneratorInput with financial data
 * @returns UseInsightsReturn with insights array and performance metrics
 */
export function useInsights(input: InsightGeneratorInput): UseInsightsReturn {
  const generationTimeRef = useRef<number>(0);

  const insights = useMemo(() => {
    const startTime = performance.now();

    // Early return for empty data
    if (input.currentPeriodTotal === 0 && !input.clientAggregations?.length) {
      generationTimeRef.current = performance.now() - startTime;
      return [];
    }

    const allInsights: Insight[] = [];

    // 1. YoY Growth Insight
    const yoyInsight = generateYoYGrowthInsight(
      input.currentPeriodTotal,
      input.samePeriodLastYear
    );
    if (yoyInsight) {
      allInsights.push(yoyInsight);
    }

    // 2. Client Concentration Insights
    const concentrationInsights = generateClientConcentrationInsights(
      input.clientAggregations,
      input.currentPeriodTotal
    );
    allInsights.push(...concentrationInsights);

    // 3. Margin Health Insights
    const marginInsights = generateMarginHealthInsights(input.clientAggregations);
    allInsights.push(...marginInsights);

    generationTimeRef.current = performance.now() - startTime;

    return allInsights;
  }, [
    input.currentPeriodTotal,
    input.samePeriodLastYear,
    input.clientAggregations,
    input.context,
  ]);

  return {
    insights,
    isGenerating: false,
    generationTimeMs: generationTimeRef.current,
  };
}

export default useInsights;
